import * as BABYLON from 'babylonjs';
import {
    getAdvancedMaterialTuning,
    type AdvancedMaterialParallaxQuality,
    type AdvancedMaterialPreset
} from './AdvancedMaterialPresets';

const MATERIAL_ID_PREFIX = 'AdvancedTexture_';
const BLEND_SHARPNESS = 4;

// Code-only pilot switch. Disabling this restores the original triplanar
// sampling path for every advanced material. Only presets with an explicit
// displacement map can enter the parallax path.
export const ENABLE_ADVANCED_TEXTURE_PARALLAX = true;
export const ENABLE_ADVANCED_TEXTURE_REPETITION_REDUCTION = true;

const PARALLAX_SHADER_MAX_LAYERS = 48;
const PARALLAX_QUALITY_LAYERS: Record<AdvancedMaterialParallaxQuality, { min: number; max: number }> = {
    low: { min: 8, max: 16 },
    medium: { min: 12, max: 32 },
    high: { min: 20, max: PARALLAX_SHADER_MAX_LAYERS },
};
const PARALLAX_GRAZING_FADE_START = 0.08;
const PARALLAX_GRAZING_FADE_END = 0.25;
const PARALLAX_MAX_OFFSET_TEXTURE_UNITS = 0.08;
const DEFAULT_REPETITION_WARP_FREQUENCY = 0.35;
const ADVANCED_PBR_DIRECT_LIGHT_INTENSITY = 1.2;
const ADVANCED_PBR_ENVIRONMENT_INTENSITY = 0.4;

const isParallaxEnabledForPreset = (preset: AdvancedMaterialPreset, parallaxRequested: boolean): boolean =>
    ENABLE_ADVANCED_TEXTURE_PARALLAX &&
    parallaxRequested &&
    !!preset.displacementUrl &&
    !!preset.parallaxHeightMeters &&
    preset.parallaxHeightMeters > 0;

const getTintKey = (tintColor?: number | null): string =>
    tintColor == null ? 'FFFFFF' : ((tintColor >>> 8) & 0xFFFFFF).toString(16).padStart(6, '0').toUpperCase();

const getParallaxLayerRange = (
    quality: IAdvancedMaterialTuning['parallaxQuality']
): { min: number; max: number } => PARALLAX_QUALITY_LAYERS[quality ?? 'medium'];

export const getAdvancedTextureMaterialId = (
    presetId: string,
    tintColor?: number | null,
    parallaxEnabled: boolean = false
): string => `${MATERIAL_ID_PREFIX}${presetId}_${getTintKey(tintColor)}_P${parallaxEnabled ? '1' : '0'}`;

export const isAdvancedTextureMaterialForPreset = (materialId: string, presetId: string): boolean =>
    materialId.startsWith(`${MATERIAL_ID_PREFIX}${presetId}_`);

export function setAdvancedTextureRepetitionWarpFrequency(
    material: BABYLON.NodeMaterial,
    frequency: number
): boolean {
    const block = material.getBlockByName('repetitionWarpFrequency');
    if (!(block instanceof BABYLON.InputBlock)) return false;

    block.value = frequency;
    return true;
}

const setTuningInputValue = (
    material: BABYLON.NodeMaterial,
    blockName: string,
    value: number | undefined
): void => {
    if (value == null) return;
    const block = material.getBlockByName(blockName);
    if (block instanceof BABYLON.InputBlock) block.value = value;
};

export function applyAdvancedMaterialTuningToMaterial(
    material: BABYLON.NodeMaterial,
    preset: AdvancedMaterialPreset,
    requestedTuning?: IAdvancedMaterialTuning | null
): void {
    const tuning = getAdvancedMaterialTuning(preset, requestedTuning);
    const textureScale = tuning.textureScale ?? preset.textureScale;

    setTuningInputValue(material, 'textureScale', textureScale);
    setTuningInputValue(material, 'normalStrength', tuning.normalStrength);
    setTuningInputValue(material, 'roughnessScale', tuning.roughnessScale);
    setTuningInputValue(material, 'metalnessStrength', tuning.metalnessStrength);
    setTuningInputValue(material, 'triplanarBlendSharpness', tuning.triplanarBlendSharpness);
    setTuningInputValue(material, 'alphaCutoff', tuning.opacityCutoff);
    setTuningInputValue(material, 'repetitionWarpStrength', tuning.repetitionWarpStrength);
    setTuningInputValue(material, 'repetitionWarpFrequency', tuning.repetitionWarpFrequency);
    setTuningInputValue(
        material,
        'parallaxDepthTextureUnits',
        (tuning.parallaxHeightMeters ?? 0) * textureScale
    );
    const parallaxLayers = getParallaxLayerRange(tuning.parallaxQuality);
    setTuningInputValue(material, 'parallaxMinLayers', parallaxLayers.min);
    setTuningInputValue(material, 'parallaxMaxLayers', parallaxLayers.max);

    const pbr = material.getBlockByName('pbr');
    if (pbr instanceof BABYLON.PBRMetallicRoughnessBlock) {
        pbr.environmentIntensity = tuning.environmentIntensity ?? ADVANCED_PBR_ENVIRONMENT_INTENSITY;
    }
}

const getTintColor = (tintColor?: number | null): BABYLON.Color3 => {
    if (tintColor == null) return BABYLON.Color3.White();

    const packedColor = tintColor >>> 0;
    return new BABYLON.Color3(
        ((packedColor >>> 24) & 0xFF) / 255,
        ((packedColor >>> 16) & 0xFF) / 255,
        ((packedColor >>> 8) & 0xFF) / 255
    ).toLinearSpace();
};

function createTexture(url: string, scene: BABYLON.Scene, gammaSpace: boolean): BABYLON.Texture {
    const texture = new BABYLON.Texture(url, scene);
    texture.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
    texture.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
    texture.anisotropicFilteringLevel = 4;
    texture.gammaSpace = gammaSpace;
    return texture;
}

function createRepetitionReducedPosition(
    preset: AdvancedMaterialPreset,
    tuning: IAdvancedMaterialTuning,
    scaledWorldPosition: BABYLON.NodeMaterialConnectionPoint
): BABYLON.NodeMaterialConnectionPoint {
    const strengthValue = tuning.repetitionWarpStrength ?? 0;
    const frequencyValue = tuning.repetitionWarpFrequency ?? DEFAULT_REPETITION_WARP_FREQUENCY;
    if (!ENABLE_ADVANCED_TEXTURE_REPETITION_REDUCTION || strengthValue <= 0) {
        return scaledWorldPosition;
    }

    const strength = new BABYLON.InputBlock('repetitionWarpStrength');
    strength.value = strengthValue;
    strength.isConstant = false;

    const frequency = new BABYLON.InputBlock('repetitionWarpFrequency');
    frequency.value = frequencyValue;
    // Keep this value uniform-backed so the developer keyboard tuning control
    // can adjust swirl without rebuilding the material or changing offset.
    frequency.isConstant = false;

    const warp = new BABYLON.CustomBlock('repetitionReducedCoordinates');
    warp.options = {
        target: 'Fragment',
        functionName: 'vplantRepetitionReducedCoordinates',
        inParameters: [
            { name: 'position', type: 'Vector4' },
            { name: 'frequency', type: 'Float' },
            { name: 'strength', type: 'Float' },
        ],
        outParameters: [
            { name: 'warpedPosition', type: 'Vector4' },
        ],
        code: [
            'vec3 vplantHash33(vec3 p) {',
            '    p = fract(p * vec3(0.1031, 0.1030, 0.0973));',
            '    p += dot(p, p.yxz + 33.33);',
            '    return fract((p.xxy + p.yxx) * p.zyx);',
            '}',
            '',
            'vec3 vplantSmoothRandomOffset(vec3 p) {',
            '    vec3 cell = floor(p);',
            '    vec3 blend = fract(p);',
            '    blend = blend * blend * (3.0 - 2.0 * blend);',
            '',
            '    vec3 z00 = mix(vplantHash33(cell + vec3(0.0, 0.0, 0.0)), vplantHash33(cell + vec3(0.0, 0.0, 1.0)), blend.z);',
            '    vec3 z10 = mix(vplantHash33(cell + vec3(1.0, 0.0, 0.0)), vplantHash33(cell + vec3(1.0, 0.0, 1.0)), blend.z);',
            '    vec3 z01 = mix(vplantHash33(cell + vec3(0.0, 1.0, 0.0)), vplantHash33(cell + vec3(0.0, 1.0, 1.0)), blend.z);',
            '    vec3 z11 = mix(vplantHash33(cell + vec3(1.0, 1.0, 0.0)), vplantHash33(cell + vec3(1.0, 1.0, 1.0)), blend.z);',
            '    return mix(mix(z00, z10, blend.x), mix(z01, z11, blend.x), blend.y);',
            '}',
            '',
            'void vplantRepetitionReducedCoordinates(vec4 position, float frequency, float strength, out vec4 warpedPosition) {',
            '    vec3 randomOffset = vplantSmoothRandomOffset(position.xyz * frequency) * 2.0 - 1.0;',
            '    warpedPosition = vec4(position.xyz + randomOffset * strength, position.w);',
            '}',
        ],
    };

    scaledWorldPosition.connectTo((warp as any).position);
    frequency.output.connectTo((warp as any).frequency);
    strength.output.connectTo((warp as any).strength);

    return (warp as any).warpedPosition;
}

function createTriPlanarBlock(
    name: string,
    texture: BABYLON.Texture,
    scaledWorldPosition: BABYLON.NodeMaterialConnectionPoint,
    worldNormal: BABYLON.NodeMaterialConnectionPoint,
    sharpness: BABYLON.NodeMaterialConnectionPoint,
    convertToLinearSpace: boolean
): BABYLON.TriPlanarBlock {
    const block = new BABYLON.TriPlanarBlock(name);
    block.texture = texture;
    block.projectAsCube = true;
    block.convertToLinearSpace = convertToLinearSpace;

    scaledWorldPosition.connectTo(block.position);
    worldNormal.connectTo(block.normal);
    sharpness.connectTo(block.sharpness);

    return block;
}

function createTriplanarNormalBlock(
    scene: BABYLON.Scene,
    preset: AdvancedMaterialPreset,
    tuning: IAdvancedMaterialTuning,
    scaledWorldPosition: BABYLON.NodeMaterialConnectionPoint,
    worldNormal: BABYLON.NodeMaterialConnectionPoint,
    sharpness: BABYLON.NodeMaterialConnectionPoint
): BABYLON.NodeMaterialConnectionPoint | null {
    if (!preset.normalUrl) return null;

    const normalSource = new BABYLON.ImageSourceBlock('normalMapSource');
    normalSource.texture = createTexture(preset.normalUrl, scene, false);

    const strength = new BABYLON.InputBlock('normalStrength');
    strength.value = tuning.normalStrength ?? preset.normalStrength;
    strength.isConstant = false;

    const triplanarNormal = new BABYLON.CustomBlock('triplanarNormal');
    triplanarNormal.options = {
        target: 'Fragment',
        functionName: 'vplantTriplanarNormal',
        inParameters: [
            { name: 'normalSampler', type: 'sampler2D' },
            { name: 'position', type: 'Vector4' },
            { name: 'surfaceNormal', type: 'Vector4' },
            { name: 'strength', type: 'Float' },
            { name: 'sharpness', type: 'Float' },
        ],
        outParameters: [
            { name: 'perturbedNormal', type: 'Vector4' },
        ],
        code: [
            'void vplantTriplanarNormal(sampler2D normalSampler, vec4 position, vec4 surfaceNormal, float strength, float sharpness, out vec4 perturbedNormal) {',
            '    vec3 n = normalize(surfaceNormal.xyz);',
            '    float signX = n.x < 0.0 ? -1.0 : 1.0;',
            '    float signY = n.y < 0.0 ? -1.0 : 1.0;',
            '    float signZ = n.z < 0.0 ? -1.0 : 1.0;',
            '',
            '    vec2 uvX = vec2(-signX * position.z, position.y);',
            '    vec2 uvY = vec2(position.z, signY * position.x);',
            '    vec2 uvZ = vec2(signZ * position.x, position.y);',
            '',
            '    vec2 mapX = texture2D(normalSampler, uvX).rg * 2.0 - 1.0;',
            '    vec2 mapY = texture2D(normalSampler, uvY).rg * 2.0 - 1.0;',
            '    vec2 mapZ = texture2D(normalSampler, uvZ).rg * 2.0 - 1.0;',
            '',
            '    vec3 perturbX = vec3(0.0, mapX.y, -signX * mapX.x);',
            '    vec3 perturbY = vec3(signY * mapY.y, 0.0, mapY.x);',
            '    vec3 perturbZ = vec3(signZ * mapZ.x, mapZ.y, 0.0);',
            '',
            '    vec3 weights = pow(abs(n), vec3(max(sharpness, 0.0001)));',
            '    weights /= max(weights.x + weights.y + weights.z, 0.0001);',
            '    vec3 perturbation = perturbX * weights.x + perturbY * weights.y + perturbZ * weights.z;',
            '    perturbation -= n * dot(perturbation, n);',
            '    perturbedNormal = vec4(normalize(n + perturbation * strength), 0.0);',
            '}',
        ],
    };

    normalSource.source.connectTo((triplanarNormal as any).normalSampler);
    scaledWorldPosition.connectTo((triplanarNormal as any).position);
    worldNormal.connectTo((triplanarNormal as any).surfaceNormal);
    strength.output.connectTo((triplanarNormal as any).strength);
    sharpness.connectTo((triplanarNormal as any).sharpness);

    return (triplanarNormal as any).perturbedNormal;
}

function createTriplanarParallaxBlock(
    scene: BABYLON.Scene,
    preset: AdvancedMaterialPreset,
    tuning: IAdvancedMaterialTuning,
    parallaxRequested: boolean,
    scaledWorldPosition: BABYLON.NodeMaterialConnectionPoint,
    worldPosition: BABYLON.NodeMaterialConnectionPoint,
    worldNormal: BABYLON.NodeMaterialConnectionPoint,
    cameraPosition: BABYLON.NodeMaterialConnectionPoint
): BABYLON.NodeMaterialConnectionPoint | null {
    if (!isParallaxEnabledForPreset(preset, parallaxRequested)) {
        return null;
    }

    const displacementSource = new BABYLON.ImageSourceBlock('displacementMapSource');
    displacementSource.texture = createTexture(preset.displacementUrl, scene, false);

    // The map coordinate is texture repeats/metre, while the height setting is
    // metres. Their product converts the physical height into texture units.
    const parallaxDepth = new BABYLON.InputBlock('parallaxDepthTextureUnits');
    parallaxDepth.value = (tuning.parallaxHeightMeters ?? 0) * (tuning.textureScale ?? preset.textureScale);
    parallaxDepth.isConstant = false;

    const parallaxLayers = getParallaxLayerRange(tuning.parallaxQuality);
    const parallaxMinLayers = new BABYLON.InputBlock('parallaxMinLayers');
    parallaxMinLayers.value = parallaxLayers.min;
    parallaxMinLayers.isConstant = false;
    const parallaxMaxLayers = new BABYLON.InputBlock('parallaxMaxLayers');
    parallaxMaxLayers.value = parallaxLayers.max;
    parallaxMaxLayers.isConstant = false;

    const parallax = new BABYLON.CustomBlock('triplanarParallax');
    parallax.options = {
        target: 'Fragment',
        functionName: 'vplantTriplanarParallax',
        inParameters: [
            { name: 'displacementSampler', type: 'sampler2D' },
            { name: 'position', type: 'Vector4' },
            { name: 'worldPosition', type: 'Vector4' },
            { name: 'surfaceNormal', type: 'Vector4' },
            { name: 'cameraPosition', type: 'Vector3' },
            { name: 'parallaxDepth', type: 'Float' },
            { name: 'minLayers', type: 'Float' },
            { name: 'maxLayers', type: 'Float' },
        ],
        outParameters: [
            { name: 'parallaxPosition', type: 'Vector4' },
        ],
        code: [
            'void vplantTriplanarParallax(sampler2D displacementSampler, vec4 position, vec4 worldPosition, vec4 surfaceNormal, vec3 cameraPosition, float parallaxDepth, float minLayers, float maxLayers, out vec4 parallaxPosition) {',
            '    vec3 n = normalize(surfaceNormal.xyz);',
            '    vec3 viewDirection = normalize(cameraPosition - worldPosition.xyz);',
            '    vec3 weights = abs(n);',
            '    vec2 baseUv;',
            '    vec2 viewUv;',
            '    float viewNormal;',
            '    float axisSign;',
            '    int dominantAxis;',
            '',
            '    if (weights.x >= weights.y && weights.x >= weights.z) {',
            '        dominantAxis = 0;',
            '        axisSign = n.x < 0.0 ? -1.0 : 1.0;',
            '        baseUv = vec2(-axisSign * position.z, position.y);',
            '        viewUv = vec2(-axisSign * viewDirection.z, viewDirection.y);',
            '        viewNormal = abs(viewDirection.x);',
            '    } else if (weights.y >= weights.z) {',
            '        dominantAxis = 1;',
            '        axisSign = n.y < 0.0 ? -1.0 : 1.0;',
            '        baseUv = vec2(position.z, axisSign * position.x);',
            '        viewUv = vec2(viewDirection.z, axisSign * viewDirection.x);',
            '        viewNormal = abs(viewDirection.y);',
            '    } else {',
            '        dominantAxis = 2;',
            '        axisSign = n.z < 0.0 ? -1.0 : 1.0;',
            '        baseUv = vec2(axisSign * position.x, position.y);',
            '        viewUv = vec2(axisSign * viewDirection.x, viewDirection.y);',
            '        viewNormal = abs(viewDirection.z);',
            '    }',
            '',
            `    float grazingFade = smoothstep(${PARALLAX_GRAZING_FADE_START.toFixed(2)}, ${PARALLAX_GRAZING_FADE_END.toFixed(2)}, viewNormal);`,
            '    float layerCount = mix(maxLayers, minLayers, clamp(viewNormal, 0.0, 1.0));',
            '    float layerDepth = 1.0 / layerCount;',
            '    float safeViewNormal = max(viewNormal, 0.05);',
            '    vec2 totalOffset = (viewUv / safeViewNormal) * parallaxDepth * grazingFade;',
            `    float offsetLength = length(totalOffset);`,
            `    if (offsetLength > ${PARALLAX_MAX_OFFSET_TEXTURE_UNITS.toFixed(2)}) totalOffset *= ${PARALLAX_MAX_OFFSET_TEXTURE_UNITS.toFixed(2)} / offsetLength;`,
            '    vec2 uvStep = totalOffset / layerCount;',
            '',
            '    vec2 currentUv = baseUv;',
            '    float currentLayerDepth = 0.0;',
            '    float currentMapDepth = 1.0 - texture2D(displacementSampler, currentUv).r;',
            `    for (int i = 0; i < ${PARALLAX_SHADER_MAX_LAYERS}; ++i) {`,
            '        if (float(i) >= layerCount || currentLayerDepth >= currentMapDepth) break;',
            '        currentUv -= uvStep;',
            '        currentLayerDepth += layerDepth;',
            '        currentMapDepth = 1.0 - texture2D(displacementSampler, currentUv).r;',
            '    }',
            '',
            '    vec2 previousUv = currentUv + uvStep;',
            '    float afterDepth = currentMapDepth - currentLayerDepth;',
            '    float previousMapDepth = 1.0 - texture2D(displacementSampler, previousUv).r;',
            '    float beforeDepth = previousMapDepth - currentLayerDepth + layerDepth;',
            '    float denominator = afterDepth - beforeDepth;',
            '    float interpolation = abs(denominator) > 0.0001 ? clamp(afterDepth / denominator, 0.0, 1.0) : 0.0;',
            '    vec2 finalUv = mix(currentUv, previousUv, interpolation);',
            '',
            '    vec4 adjustedPosition = position;',
            '    if (dominantAxis == 0) {',
            '        adjustedPosition.z = -axisSign * finalUv.x;',
            '        adjustedPosition.y = finalUv.y;',
            '    } else if (dominantAxis == 1) {',
            '        adjustedPosition.z = finalUv.x;',
            '        adjustedPosition.x = axisSign * finalUv.y;',
            '    } else {',
            '        adjustedPosition.x = axisSign * finalUv.x;',
            '        adjustedPosition.y = finalUv.y;',
            '    }',
            '    parallaxPosition = adjustedPosition;',
            '}',
        ],
    };

    displacementSource.source.connectTo((parallax as any).displacementSampler);
    scaledWorldPosition.connectTo((parallax as any).position);
    worldPosition.connectTo((parallax as any).worldPosition);
    worldNormal.connectTo((parallax as any).surfaceNormal);
    cameraPosition.connectTo((parallax as any).cameraPosition);
    parallaxDepth.output.connectTo((parallax as any).parallaxDepth);
    parallaxMinLayers.output.connectTo((parallax as any).minLayers);
    parallaxMaxLayers.output.connectTo((parallax as any).maxLayers);

    return (parallax as any).parallaxPosition;
}

/**
 * Creates the hard-coded UV-free material used by the advanced-texture pilot.
 * The material samples the supplied PBR maps in world space on three axes.
 */
export function createTriplanarPbrMaterial(
    scene: BABYLON.Scene,
    preset: AdvancedMaterialPreset,
    tintColor?: number | null,
    parallaxRequested: boolean = false,
    requestedTuning?: IAdvancedMaterialTuning | null
): BABYLON.NodeMaterial {
    const tuning = getAdvancedMaterialTuning(preset, requestedTuning);
    const materialId = getAdvancedTextureMaterialId(preset.id, tintColor, parallaxRequested);
    const material = new BABYLON.NodeMaterial(materialId, scene);
    material.id = materialId;
    material.backFaceCulling = !preset.doubleSided;

    const position = new BABYLON.InputBlock('position');
    position.setAsAttribute(BABYLON.VertexBuffer.PositionKind);

    const normal = new BABYLON.InputBlock('normal');
    normal.setAsAttribute(BABYLON.VertexBuffer.NormalKind);

    const world = new BABYLON.InputBlock('world');
    world.setAsSystemValue(BABYLON.NodeMaterialSystemValues.World);

    // Resolves to the ordinary world matrix for regular meshes and folds in
    // Babylon's per-instance matrix for instances and thin instances.
    const instances = new BABYLON.InstancesBlock('instances');
    world.output.connectTo(instances.world);
    instances.autoConfigure(material);

    const viewProjection = new BABYLON.InputBlock('viewProjection');
    viewProjection.setAsSystemValue(BABYLON.NodeMaterialSystemValues.ViewProjection);

    const view = new BABYLON.InputBlock('view');
    view.setAsSystemValue(BABYLON.NodeMaterialSystemValues.View);

    const cameraPosition = new BABYLON.InputBlock('cameraPosition');
    cameraPosition.setAsSystemValue(BABYLON.NodeMaterialSystemValues.CameraPosition);

    const worldPosition = new BABYLON.TransformBlock('worldPosition');
    worldPosition.complementZ = 0;
    worldPosition.complementW = 1;
    position.output.connectTo(worldPosition.vector);
    instances.output.connectTo(worldPosition.transform);

    const worldNormal = new BABYLON.TransformBlock('worldNormal');
    worldNormal.complementZ = 0;
    worldNormal.complementW = 0;
    normal.output.connectTo(worldNormal.vector);
    instances.output.connectTo(worldNormal.transform);

    const clipPosition = new BABYLON.TransformBlock('clipPosition');
    clipPosition.complementZ = 0;
    clipPosition.complementW = 1;
    worldPosition.output.connectTo(clipPosition.vector);
    viewProjection.output.connectTo(clipPosition.transform);

    const vertexOutput = new BABYLON.VertexOutputBlock('vertexOutput');
    clipPosition.output.connectTo(vertexOutput.vector);

    const textureScale = new BABYLON.InputBlock('textureScale');
    textureScale.value = tuning.textureScale ?? preset.textureScale;
    textureScale.isConstant = false;

    const scaledWorldPosition = new BABYLON.ScaleBlock('scaledWorldPosition');
    worldPosition.output.connectTo(scaledWorldPosition.input);
    textureScale.output.connectTo(scaledWorldPosition.factor);

    // Apply one continuous world-space coordinate warp before every material
    // sample. The displacement ray and all PBR maps therefore remain aligned.
    const repetitionReducedPosition = createRepetitionReducedPosition(preset, tuning, scaledWorldPosition.output);

    const materialSamplePosition = createTriplanarParallaxBlock(
        scene,
        preset,
        tuning,
        parallaxRequested,
        repetitionReducedPosition,
        worldPosition.output,
        worldNormal.output,
        cameraPosition.output
    ) ?? repetitionReducedPosition;

    const sharpness = new BABYLON.InputBlock('triplanarBlendSharpness');
    sharpness.value = tuning.triplanarBlendSharpness ?? BLEND_SHARPNESS;
    sharpness.isConstant = false;

    const colorMap = createTriPlanarBlock(
        'colorMap',
        createTexture(preset.colorUrl, scene, true),
        materialSamplePosition,
        worldNormal.output,
        sharpness.output,
        true
    );
    const metalnessMap = preset.metalnessUrl ? createTriPlanarBlock(
        'metalnessMap',
        createTexture(preset.metalnessUrl, scene, false),
        materialSamplePosition,
        worldNormal.output,
        sharpness.output,
        false
    ) : null;
    const roughnessMap = createTriPlanarBlock(
        'roughnessMap',
        createTexture(preset.roughnessUrl, scene, false),
        materialSamplePosition,
        worldNormal.output,
        sharpness.output,
        false
    );
    const perturbedWorldNormal = createTriplanarNormalBlock(
        scene,
        preset,
        tuning,
        materialSamplePosition,
        worldNormal.output,
        sharpness.output
    );

    const albedoTint = new BABYLON.InputBlock(
        'albedoTint',
        BABYLON.NodeMaterialBlockTargets.Fragment,
        BABYLON.NodeMaterialBlockConnectionPointTypes.Color3
    );
    albedoTint.value = getTintColor(tintColor);
    albedoTint.isConstant = true;

    const tintedAlbedo = new BABYLON.MultiplyBlock('tintedAlbedo');
    colorMap.rgb.connectTo(tintedAlbedo.left);
    albedoTint.output.connectTo(tintedAlbedo.right);

    const pbr = new BABYLON.PBRMetallicRoughnessBlock('pbr');
    pbr.lightFalloff = BABYLON.PBRBaseMaterial.LIGHTFALLOFF_PHYSICAL;
    pbr.directIntensity = ADVANCED_PBR_DIRECT_LIGHT_INTENSITY;
    pbr.environmentIntensity = tuning.environmentIntensity ?? ADVANCED_PBR_ENVIRONMENT_INTENSITY;
    pbr.useEnergyConservation = true;
    pbr.useRadianceOcclusion = true;
    pbr.useHorizonOcclusion = true;
    pbr.forceNormalForward = true;

    worldPosition.output.connectTo(pbr.worldPosition);
    (perturbedWorldNormal ?? worldNormal.output).connectTo(pbr.worldNormal);
    view.output.connectTo(pbr.view);
    cameraPosition.output.connectTo(pbr.cameraPosition);
    tintedAlbedo.output.connectTo(pbr.baseColor);
    if (metalnessMap) {
        const metalnessStrength = new BABYLON.InputBlock('metalnessStrength');
        metalnessStrength.value = tuning.metalnessStrength ?? 1;
        metalnessStrength.isConstant = false;
        const scaledMetalness = new BABYLON.MultiplyBlock('scaledMetalness');
        metalnessMap.r.connectTo(scaledMetalness.left);
        metalnessStrength.output.connectTo(scaledMetalness.right);
        scaledMetalness.output.connectTo(pbr.metallic);
    } else {
        const nonMetallic = new BABYLON.InputBlock('nonMetallic');
        nonMetallic.value = 0;
        nonMetallic.isConstant = true;
        nonMetallic.output.connectTo(pbr.metallic);
    }
    const roughnessScale = new BABYLON.InputBlock('roughnessScale');
    roughnessScale.value = tuning.roughnessScale ?? 1;
    roughnessScale.isConstant = false;

    const scaledRoughness = new BABYLON.MultiplyBlock('scaledRoughness');
    roughnessMap.r.connectTo(scaledRoughness.left);
    roughnessScale.output.connectTo(scaledRoughness.right);
    scaledRoughness.output.connectTo(pbr.roughness);

    // Metallic surfaces receive most of their visible light from image-based
    // reflections rather than diffuse lighting. Use the viewer's prefiltered
    // environment map for both specular reflections and diffuse irradiance.
    const reflection = new BABYLON.ReflectionBlock('environmentReflection');
    reflection.useSphericalHarmonics = true;
    // The triplanar normal is calculated per fragment, so irradiance must also
    // be evaluated there. Vertex irradiance cannot consume a fragment output.
    reflection.forceIrradianceInFragment = true;
    position.output.connectTo(reflection.position);
    instances.output.connectTo(reflection.world);
    reflection.reflection.connectTo(pbr.reflection);

    const fragmentOutput = new BABYLON.FragmentOutputBlock('fragmentOutput');
    pbr.lighting.connectTo(fragmentOutput.rgb);

    material.addOutputNode(vertexOutput);
    material.addOutputNode(fragmentOutput);

    if (preset.opacityUrl) {
        const opacityMap = createTriPlanarBlock(
            'opacityMap',
            createTexture(preset.opacityUrl, scene, false),
            materialSamplePosition,
            worldNormal.output,
            sharpness.output,
            false
        );
        const alphaCutoff = new BABYLON.InputBlock('alphaCutoff');
        alphaCutoff.value = tuning.opacityCutoff ?? preset.alphaCutoff ?? 0.5;
        alphaCutoff.isConstant = false;

        const discard = new BABYLON.DiscardBlock('opacityCutout');
        opacityMap.r.connectTo(discard.value);
        alphaCutoff.output.connectTo(discard.cutoff);
        material.addOutputNode(discard);
    }

    material.build();
    return material;
}
